import React, { useState, useEffect } from "react";
import ReactDOM from 'react-dom/client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Fab,
  useMediaQuery,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import BlockIcon from "@mui/icons-material/Block";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import dayjs from "dayjs"; // If not installed, run: npm install dayjs

function ManagerHome() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    studio: "",
    username: "",
    password: "",
    mobile: "",
    place: "",
    studiologo: "" // <-- frontend field
  });
  const [logoFile, setLogoFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [clients, setClients] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    active: 0,
    blocked: 0
  });
  const [showActiveDialog, setShowActiveDialog] = useState(false);
  const [showBlockedDialog, setShowBlockedDialog] = useState(false);
  const [showPaidDialog, setShowPaidDialog] = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [activeSearch, setActiveSearch] = useState("");
  const [blockedSearch, setBlockedSearch] = useState("");
  const [paidSearch, setPaidSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetch("https://studio-lc.onrender.com/api/clients")
      .then(res => res.json())
      .then(data => {
        setClients(data);
        setCounts({
          total: data.length,
          active: data.filter(c => c.status === "Active").length,
          blocked: data.filter(c => c.status === "Blocked").length
        });
      });
  }, [open, snackbar.open]); // refetch when dialog closes or snackbar opens

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogoFile(file);
    // Optionally preview or convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm({ ...form, studiologo: reader.result }); // base64 string
    };
    if (file) reader.readAsDataURL(file);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreate = async () => {
    try {
      const response = await fetch("https://studio-lc.onrender.com/api/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        setForm({ name: "", studio: "", username: "", password: "", mobile: "", place: "" });
        handleClose();
        setSnackbar({
          open: true,
          message: "Client added successfully!",
          severity: "success"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to add client.",
          severity: "error"
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Server error.",
        severity: "error"
      });
    }
  };

  const handleClientsClick = () => {
    navigate("/manager-dashboard/clients");
  };

  const handleActiveClientsClick = () => setShowActiveDialog(true);
  const handleBlockedClientsClick = () => setShowBlockedDialog(true);

  const handleStatusChange = async (client) => {
    const newStatus = client.status === "Active" ? "Blocked" : "Active";
    await fetch(`https://studio-lc.onrender.com/api/clients/${client._id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...client, status: newStatus })
    });
    // Refetch clients
    fetch("https://studio-lc.onrender.com/api/clients")
      .then(res => res.json())
      .then(data => {
        setClients(data);
        setCounts({
          total: data.length,
          active: data.filter(c => c.status === "Active").length,
          blocked: data.filter(c => c.status === "Blocked").length
        });
      });
  };

  const today = dayjs();
  const pendingOrExpiringClients = clients.filter(c => {
    const isPending = c.paymentState === "Pending";
    const expiryDiff = c.planEnd ? dayjs(c.planEnd).diff(today, "day") : null;
    // Show if pending OR planEnd is within 10 days (including already expired)
    const isExpiringOrExpired = expiryDiff !== null && expiryDiff <= 10;
    return isPending || isExpiringOrExpired;
  });

  const cards = [
    {
      label: "Add Client",
      icon: (
        <IconButton color="primary" sx={{ bgcolor: "#fff", boxShadow: 2 }}>
          <AddIcon fontSize="large" />
        </IconButton>
      ),
      count: null,
      color: "#1976d2",
      gradient: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
    },
    {
      label: "Number of Clients",
      icon: <GroupsIcon fontSize="large" sx={{ color: "#1976d2", mb: 1 }} />,
      count: counts.total,
      color: "#1976d2",
      gradient: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      onClick: handleClientsClick
    },
    {
      label: "Active Clients",
      icon: <PersonAddAlt1Icon fontSize="large" sx={{ color: "#388e3c", mb: 1 }} />,
      count: counts.active,
      color: "#388e3c",
      gradient: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
      onClick: handleActiveClientsClick
    },
    {
      label: "Blocked Clients",
      icon: <BlockIcon fontSize="large" sx={{ color: "#d32f2f", mb: 1 }} />,
      count: counts.blocked,
      color: "#d32f2f",
      gradient: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
      onClick: handleBlockedClientsClick
    },
    {
      label: "Paid Clients",
      icon: <GroupsIcon fontSize="large" sx={{ color: "#43ea7b", mb: 1 }} />,
      count: clients.filter(c => c.paymentState === "Paid").length,
      color: "#43ea7b",
      gradient: "linear-gradient(135deg, #e0f7fa 0%, #b2dfdb 100%)",
      onClick: () => setShowPaidDialog(true)
    },
    {
      label: "Pending Payment ",
      icon: <GroupsIcon fontSize="large" sx={{ color: "#ffd600", mb: 1 }} />,
      count: clients.filter(c => c.paymentState === "Pending").length,
      color: "#ffd600",
      gradient: "linear-gradient(135deg, #fffde7 0%, #ffe082 100%)",
      onClick: () => setShowPendingDialog(true)
    }
  ];

  return (
    <Box sx={{ p: isMobile ? 1 : 4,  backgroundImage: `url('https://wallpaperaccess.com/full/391083.jpg')`, minHeight: "100vh", width: "95vw", position: "relative", marginTop: isMobile ? 0 : "0px" }}>
      <Grid container spacing={isMobile ? 2 : 4}>
        <Box
  sx={{
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "stretch",
    gap: 4,
    mb: 4,
    overflowX: { xs: "auto", md: "visible" },
    pb: 2,
    px: 1,
    width: "100%",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": { height: 8 },
    "&::-webkit-scrollbar-thumb": { bgcolor: "#bbdefb", borderRadius: 4 }
  }}
>
  {cards.map((card, idx) => (
    <Card
      key={card.label}
      sx={{
        minWidth: 180,
        maxWidth: 220,
        height: 180,
        flex: "0 0 260px",
        borderRadius: 5,
        boxShadow: "0 4px 24px #1976d222",
        textAlign: "center",
        background: card.gradient,
        transition: "transform 0.2s, box-shadow 0.2s",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: card.onClick ? "pointer" : "default",
        border: "2px solid #fff",
        position: "relative",
        "&:hover": {
          transform: "translateY(-8px) scale(1.04)",
          boxShadow: "0 8px 32px #1976d244",
          borderColor: "#1976d2"
        },
        mx: 1
      }}
      onClick={card.onClick ? card.onClick : card.label === "Add Client" ? handleOpen : card.label === "Number of Clients" ? handleClientsClick : undefined}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
          {card.icon}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: card.color,  fontFamily: "Josefin Sans",fontSize:"20px" }}>
          {card.label}
        </Typography>
        {card.count !== null && (
          <Typography variant="h4" sx={{ color: card.color, fontWeight: 800 }}>
            {card.count}
          </Typography>
        )}
      </CardContent>
    </Card>
  ))}
</Box>
      </Grid>

      {/* Add Client Form Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : 400,
            maxWidth: "95vw",
            maxHeight: isMobile ? "80vh" : "auto",
            m: "auto",
            borderRadius: 3,
            bgcolor: "#f5f5f5",
            p: 2,
            boxShadow: 6,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "#fff",
            textAlign: "center",
            fontWeight: 700,
            fontSize: 22,
             fontFamily: "Josefin Sans",
            py: 2,
            borderRadius: "12px 12px 0 0"
          }}
        >
          Add Client
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* Studio Logo Upload */}
            <Button
              variant="outlined"
              component="label"
              sx={{ alignSelf: "flex-start", mb: 1 , fontFamily: "Josefin Sans",}}
            >
              Upload Studio Logo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleLogoChange}
              />
            </Button>
            {form.studiologo && (
              <Box sx={{ mb: 1 }}>
                <img
                  src={form.studiologo}
                  alt="Studio Logo Preview"
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 8,
                    objectFit: "cover",
                    boxShadow: "0 2px 8px #bbb"
                  }}
                />
              </Box>
            )}
            <TextField
              margin="dense"
              label="Name"
              name="name"
              fullWidth
              value={form.name}
              onChange={handleChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              label="Studio Name"
              name="studio"
              fullWidth
              value={form.studio}
              onChange={handleChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              label="User Name"
              name="username"
              fullWidth
              value={form.username}
              onChange={handleChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              label="Password"
              name="password"
              fullWidth
              value={form.password}
              onChange={handleChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              label="Mobile No"
              name="mobile"
              fullWidth
              value={form.mobile}
              onChange={handleChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              label="Place"
              name="place"
              fullWidth
              value={form.place}
              onChange={handleChange}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={handleClose} sx={{ px: 4, fontWeight: 700, fontFamily: "Josefin Sans", }}>Cancel</Button>
          <Button  variant="contained" onClick={handleCreate} sx={{ px: 4, fontWeight: 700, bgcolor: "#1976d2", fontFamily: "Josefin Sans", }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Active Clients Dialog */}
      <Dialog open={showActiveDialog} onClose={() => setShowActiveDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: "#e8f5e9", color: "#388e3c", fontWeight: 700, fontFamily: "Josefin Sans", }}>Active Clients</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
            <TextField
              variant="outlined"
              placeholder="Search by name or studio"
              value={activeSearch}
              onChange={e => setActiveSearch(e.target.value)}
              size="small"
              sx={{
                width: "100%",
                maxWidth: 600,
                mt:3,
                bgcolor: "#fff",
                borderRadius: 5,
                boxShadow: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 5,
                  fontSize: 16,
                  "& fieldset": { borderColor: "#388e3c" },
                  "&:hover fieldset": { borderColor: "#1976d2" },
                  "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 2 }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Stack spacing={2}>
            {clients
              .filter(c => c.status === "Active")
              .filter(c =>
                c.name.toLowerCase().includes(activeSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(activeSearch.toLowerCase())
              )
              .map((client, idx) => (
                <Box
                  key={client._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: "#f1f8e9",
                    borderRadius: 2,
                    mt: idx === 0 ? 0.6 : 0 // Move first row down 5px
                  }}
                >
                  <Typography sx={{ fontWeight: 600,  fontFamily: "Jost", }}>{client.name} ({client.studio})</Typography>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleStatusChange(client)}
                    sx={{
                      fontWeight: 700,
                      borderRadius: 3,
                      px: 3,
                       fontFamily: "Josefin Sans",
                      py: 1,
                      background: "linear-gradient(90deg,#ff5252 0%,#ff1744 100%)",
                      color: "#fff",
                      boxShadow: 3,
                      transition: "all 0.2s",
                      "&:hover": {
                        background: "linear-gradient(90deg,#d50000 0%,#ff1744 100%)",
                        boxShadow: 6,
                        transform: "scale(1.05)"
                      }
                    }}
                  >
                    Block
                  </Button>
                </Box>
              ))}
            {clients.filter(c => c.status === "Active")
              .filter(c =>
                c.name.toLowerCase().includes(activeSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(activeSearch.toLowerCase())
              ).length === 0 && (
              <Typography sx={{ textAlign: "center", color: "#888", mt: 2, fontFamily: "Josefin Sans", }}>
                No clients found.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowActiveDialog(false)}
            variant="contained"
            color="primary"
            sx={{
              fontWeight: 700,
              borderRadius: 3,
              px: 4,
              boxShadow: 2,
               fontFamily: "Josefin Sans",
              background: "linear-gradient(90deg,#1976d2 0%,#64b5f6 100%)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg,#115293 0%,#1976d2 100%)"
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocked Clients Dialog */}
      <Dialog open={showBlockedDialog} onClose={() => setShowBlockedDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: "#ffebee", color: "#d32f2f", fontWeight: 700, fontFamily: "Josefin Sans", }}>Blocked Clients</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
            <TextField
              variant="outlined"
              placeholder="Search by name or studio"
              value={blockedSearch}
              onChange={e => setBlockedSearch(e.target.value)}
              size="small"
              sx={{
                width: "100%",
                maxWidth: 600,
                mt:3,
                bgcolor: "#fff",
                borderRadius: 5,
                boxShadow: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 5,
                  fontSize: 16,
                   fontFamily: "Josefin Sans",
                  "& fieldset": { borderColor: "#d32f2f" },
                  "&:hover fieldset": { borderColor: "#1976d2" },
                  "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 2 }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Stack spacing={2}>
            {clients
              .filter(c => c.status === "Blocked")
              .filter(c =>
                c.name.toLowerCase().includes(blockedSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(blockedSearch.toLowerCase())
              )
              .map((client, idx) => (
                <Box
                  key={client._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: "#ffebee",
                    borderRadius: 2,
                    mt: idx === 0 ? 0.6 : 0 // Move first row down 5px
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontFamily: "Josefin Sans", }}>{client.name} ({client.studio})</Typography>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleStatusChange(client)}
                    sx={{
                      fontWeight: 700,
                      borderRadius: 3,
                       fontFamily: "Josefin Sans",
                      px: 3,
                      py: 1,
                      background: "linear-gradient(90deg,#43ea7b 0%,#1de9b6 100%)",
                      color: "#fff",
                      boxShadow: 3,
                      transition: "all 0.2s",
                      "&:hover": {
                        background: "linear-gradient(90deg,#00bfa5 0%,#43ea7b 100%)",
                        boxShadow: 6,
                        transform: "scale(1.05)"
                      }
                    }}
                  >
                    Activate
                  </Button>
                </Box>
              ))}
            {clients.filter(c => c.status === "Blocked")
              .filter(c =>
                c.name.toLowerCase().includes(blockedSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(blockedSearch.toLowerCase())
              ).length === 0 && (
              <Typography sx={{ textAlign: "center", color: "#888", mt: 2, fontFamily: "Jost", }}>
                No clients found.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowBlockedDialog(false)}
            variant="contained"
            color="primary"
            sx={{
              fontWeight: 700,
               fontFamily: "Josefin Sans",
              borderRadius: 3,
              px: 4,
              boxShadow: 2,
              background: "linear-gradient(90deg,#1976d2 0%,#64b5f6 100%)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg,#115293 0%,#1976d2 100%)"
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paid Clients Dialog */}
      <Dialog open={showPaidDialog} onClose={() => setShowPaidDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: "#e0f7fa", color: "#43ea7b", fontWeight: 700, fontFamily: "Josefin Sans", }}>Paid Clients</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
            <TextField
              variant="outlined"
              placeholder="Search by name or studio"
              value={paidSearch}
              onChange={e => setPaidSearch(e.target.value)}
              size="small"
              sx={{
                width: "100%",
                maxWidth: 600,
                mt:3,
                bgcolor: "#fff",
                borderRadius: 5,
                boxShadow: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 5,
                  fontSize: 16,
                  "& fieldset": { borderColor: "#43ea7b" },
                  "&:hover fieldset": { borderColor: "#1976d2" },
                  "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 2 }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Stack spacing={2}>
            {clients
              .filter(c => c.paymentState === "Paid")
              .filter(c =>
                c.name.toLowerCase().includes(paidSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(paidSearch.toLowerCase())
              )
              .map((client, idx) => (
                <Box
                  key={client._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: "#e0f7fa",
                    borderRadius: 2,
                    mt: idx === 0 ? 0.6 : 0
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontFamily: "Jost", }}>{client.name} ({client.studio})</Typography>
                </Box>
              ))}
            {clients.filter(c => c.paymentState === "Paid")
              .filter(c =>
                c.name.toLowerCase().includes(paidSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(paidSearch.toLowerCase())
              ).length === 0 && (
              <Typography sx={{ textAlign: "center", color: "#888", mt: 2, fontFamily: "Josefin Sans", }}>
                No clients found.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPaidDialog(false)}
            variant="contained"
            color="primary"
            sx={{
              fontWeight: 700,
              borderRadius: 3,
              px: 4,
              boxShadow: 2,
              background: "linear-gradient(90deg,#1976d2 0%,#64b5f6 100%)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg,#115293 0%,#1976d2 100%)"
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pending Payment Clients Dialog */}
      <Dialog open={showPendingDialog} onClose={() => setShowPendingDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: "#fffde7", color: "#ffd600", fontWeight: 700, fontFamily: "Josefin Sans", }}>Pending Payment Clients</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
            <TextField
              variant="outlined"
              placeholder="Search by name or studio"
              value={pendingSearch}
              onChange={e => setPendingSearch(e.target.value)}
              size="small"
              sx={{
                width: "100%",
                maxWidth: 600,
                bgcolor: "#fff",
                mt:3,
                borderRadius: 5,
                boxShadow: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 5,
                  fontSize: 16,
                  "& fieldset": { borderColor: "#ffd600" },
                  "&:hover fieldset": { borderColor: "#1976d2" },
                  "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 2 }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Stack spacing={2}>
            {clients
              .filter(c => c.paymentState === "Pending")
              .filter(c =>
                c.name.toLowerCase().includes(pendingSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(pendingSearch.toLowerCase())
              )
              .map((client, idx) => (
                <Box
                  key={client._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: "#fffde7",
                    borderRadius: 2,
                    mt: idx === 0 ? 0.6 : 0
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontFamily: "Jost", }}>{client.name} ({client.studio})</Typography>
                </Box>
              ))}
            {clients.filter(c => c.paymentState === "Pending")
              .filter(c =>
                c.name.toLowerCase().includes(pendingSearch.toLowerCase()) ||
                c.studio.toLowerCase().includes(pendingSearch.toLowerCase())
              ).length === 0 && (
              <Typography sx={{ textAlign: "center", color: "#888", mt: 2, fontFamily: "Josefin Sans", }}>
                No clients found.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPendingDialog(false)}
            variant="contained"
            color="primary"
            sx={{
              fontWeight: 700, 
              borderRadius: 3,
               fontFamily: "Josefin Sans",
              px: 4,
              boxShadow: 2,
              background: "linear-gradient(90deg,#1976d2 0%,#64b5f6 100%)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg,#115293 0%,#1976d2 100%)"
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
        <Card
          sx={{
            width: isMobile ? "99%" : 1500, // Increased table/card width
            bgcolor: "linear-gradient(135deg, #fffde7 0%, #ffe082 100%)",
            boxShadow: 6,
            borderRadius: 4,
            p: 3,
            textAlign: "center",
            ml: isMobile ? 0 : 0
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#d32f2f", mb: 2, fontFamily: "Josefin Sans", }}>
            Pending & Expiring Clients (Next 10 Days)
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#fffde7" }}>
                  <TableCell sx={{ fontWeight: 700, fontFamily: "Josefin Sans", color: "#1976d2" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700,  fontFamily: "Josefin Sans",color: "#1976d2" }}>Studio</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: "Josefin Sans", color: "#1976d2" }}>Mobile</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: "Josefin Sans", color: "#1976d2" }}>Place</TableCell>
                  <TableCell sx={{ fontWeight: 700,  fontFamily: "Josefin Sans",color: "#d32f2f" }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: "Josefin Sans", color: "#d32f2f" }}>Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingOrExpiringClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: "#888", fontFamily: "Josefin Sans", }}>
                      No pending or expiring clients.
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingOrExpiringClients.map(client => {
                    const daysLeft = client.planEnd ? dayjs(client.planEnd).diff(today, "day") : null;
                    return (
                      <TableRow
                        key={client._id}
                        hover
                        sx={{
                          transition: "background 0.2s, transform 0.2s",
                          "&:hover": { background: "#fffde7", transform: "scale(1.01)" }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, fontFamily: "Jost", }}>{client.name}</TableCell>
                        <TableCell  sx={{ fontWeight: 600, fontFamily: "Jost", }}>{client.studio}</TableCell>
                        <TableCell  sx={{ fontWeight: 600, fontFamily: "Jost", }}>{client.mobile}</TableCell>
                        <TableCell  sx={{ fontWeight: 600, fontFamily: "Jost", }}>{client.place}</TableCell>
                        <TableCell sx={{ color: client.paymentState === "Pending" ? "#d32f2f" : "#388e3c", fontWeight: 700 }}>
                          {client.paymentState}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: daysLeft !== null && daysLeft <= 10 ? "#d32f2f" : "#388e3c",
                            fontWeight: 700,fontFamily: "Jost",
                          }}
                        >
                          {client.planEnd ? dayjs(client.planEnd).format("YYYY-MM-DD") : ""}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
}

export default ManagerHome;